// =====================================================================
//  ADMIN CONSOLE, REPORTS
// ---------------------------------------------------------------------
//  Pick a report, set a range, see the data, export it.
//
//  Reports are generated server-side (`/admin/reports/:type`) and returned
//  as `{ columns, rows }`, which is why one component can render any of
//  them. That also means the CSV an administrator downloads is exactly the
//  data the database returned, not a re-derived approximation of what
//  happened to be rendered on screen.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  CalendarDays,
  Clock,
  Download,
  FileSpreadsheet,
  Users,
  Wallet,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Chip,
  Grid,
  LoadingBlock,
  Notice,
  PageHeader,
  Panel,
  SearchInput,
  SegmentedControl,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  toast,
  useDebounced,
} from '../ui';
import {
  addDays,
  downloadCsv,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtNumber,
  matchesSearch,
  titleCase,
  toISODate,
} from '../format';

const REPORTS = [
  {
    key: 'headcount',
    label: 'Headcount',
    icon: Users,
    description: 'Every person on record with their department, role, status and salary.',
    ranged: false,
  },
  {
    key: 'attendance',
    label: 'Attendance',
    icon: Clock,
    description: 'Per-day clock in and out times with hours worked, for a date range.',
    ranged: true,
  },
  {
    key: 'leave',
    label: 'Leave',
    icon: CalendarDays,
    description: 'Leave requests with dates, duration, status and who decided.',
    ranged: true,
  },
  {
    key: 'payroll',
    label: 'Payroll register',
    icon: Wallet,
    description: 'Every payslip issued, with gross, deductions and net pay.',
    ranged: false,
  },
  {
    key: 'payroll-cost',
    label: 'Payroll cost by month',
    icon: BarChart3,
    description: 'Twelve-month rollup of gross, deductions and net payout.',
    ranged: false,
  },
  {
    key: 'department',
    label: 'Department rollup',
    icon: Users,
    description: 'Headcount, leads, average salary and monthly cost per department.',
    ranged: false,
  },
];

const RANGE_PRESETS = [
  { key: 30, label: '30 days' },
  { key: 90, label: '90 days' },
  { key: 180, label: '6 months' },
  { key: 365, label: '1 year' },
];

// Columns whose values should be formatted as money / dates when rendered.
const MONEY_KEYS = new Set([
  'salary', 'gross_salary', 'deductions', 'net_salary', 'gross', 'net',
  'averageSalary', 'salaryTotal',
]);
const DATE_KEYS = new Set(['join_date', 'date', 'start_date', 'end_date', 'pay_date']);
const TIME_KEYS = new Set(['check_in', 'check_out']);
const NUMERIC_KEYS = new Set([
  'headcount', 'active', 'leads', 'days', 'hours', 'payslips', 'share', 'year',
]);

export default function ReportsPage() {
  const [type, setType] = useState('headcount');
  const [rangeDays, setRangeDays] = useState(90);
  const [from, setFrom] = useState(toISODate(addDays(new Date(), -90)));
  const [to, setTo] = useState(toISODate());
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search, 250);
  const report = REPORTS.find((r) => r.key === type);

  const applyPreset = (days) => {
    setRangeDays(days);
    setFrom(toISODate(addDays(new Date(), -days)));
    setTo(toISODate());
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'report', type, report?.ranged ? from : null, report?.ranged ? to : null],
    queryFn: () => backendApi.admin.report(type, report?.ranged ? { from, to } : {}),
  });

  // Pinned identities so the column-mapping and filter memos are stable.
  const rows = useMemo(() => data?.rows || [], [data]);
  const rawColumns = useMemo(() => data?.columns || [], [data]);

  /* --------------------------- column mapping -------------------------- */
  const columns = useMemo(
    () =>
      rawColumns.map((key) => ({
        key,
        label: key === 'code' ? 'Employee ID' : titleCase(key),
        align: MONEY_KEYS.has(key) || NUMERIC_KEYS.has(key) ? 'right' : undefined,
        width: key === 'name' ? 200 : undefined,
        render: (row) => {
          const value = row[key];
          if (value === null || value === undefined || value === '') {
            return <span style={{ color: 'var(--ac-faint)' }}>,</span>;
          }
          if (key === 'status') return <StatusChip value={value} />;
          if (key === 'role') return <Chip>{titleCase(value)}</Chip>;
          if (MONEY_KEYS.has(key)) return fmtMoney(value);
          if (TIME_KEYS.has(key)) return fmtDateTime(value);
          if (DATE_KEYS.has(key)) return fmtDate(value);
          if (key === 'share') return `${value}%`;
          if (key === 'hours') return Number(value).toFixed(2);
          return String(value);
        },
        csv: (row) => row[key],
      })),
    [rawColumns]
  );

  const filtered = useMemo(
    () => (debouncedSearch ? rows.filter((row) => matchesSearch(row, debouncedSearch, rawColumns)) : rows),
    [rows, debouncedSearch, rawColumns]
  );

  /* ------------------------------- totals ------------------------------ */
  // Sum any money column so the export and the screen agree on the bottom line.
  const totals = useMemo(() => {
    const moneyCols = rawColumns.filter((c) => MONEY_KEYS.has(c));
    if (!moneyCols.length) return null;
    return moneyCols.reduce((acc, col) => {
      acc[col] = filtered.reduce((sum, row) => sum + (Number(row[col]) || 0), 0);
      return acc;
    }, {});
  }, [filtered, rawColumns]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const suffix = report?.ranged ? `${from}_to_${to}` : toISODate();
    downloadCsv(`${type}-${suffix}`, filtered, columns);
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Reports"
        sub="Pull the data you need out of the HRMS and export it as a spreadsheet."
        actions={
          <Button variant="primary" icon={Download} onClick={exportCsv} disabled={!filtered.length}>
            Export CSV
          </Button>
        }
      />

      {/* ---------------- REPORT PICKER ---------------- */}
      <Grid cols={3} style={{ marginBottom: 14 }}>
        {REPORTS.map((item) => {
          const isActive = item.key === type;
          return (
            <button
              key={item.key}
              type="button"
              className="ac-kpi"
              onClick={() => setType(item.key)}
              style={{
                textAlign: 'left',
                gap: 6,
                borderColor: isActive ? 'var(--ac-accent)' : undefined,
                background: isActive ? 'var(--ac-accent-soft)' : undefined,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  className="ac-kpi-icon"
                  style={
                    isActive
                      ? { background: '#fff', borderColor: 'var(--ac-accent-border)', color: 'var(--ac-accent)' }
                      : undefined
                  }
                >
                  <item.icon size={13} strokeWidth={2} />
                </span>
                <span style={{ fontSize: 13, fontWeight: 650, color: 'var(--ac-ink)' }}>{item.label}</span>
                {item.ranged ? <Chip tone="neutral">Date range</Chip> : null}
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ac-muted)', lineHeight: 1.5 }}>
                {item.description}
              </p>
            </button>
          );
        })}
      </Grid>

      {/* ---------------- RANGE ---------------- */}
      {report?.ranged ? (
        <Panel style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
            <span className="ac-eyebrow">Range</span>
            <SegmentedControl items={RANGE_PRESETS} value={rangeDays} onChange={applyPreset} />
            <span style={{ color: 'var(--ac-faint)', fontSize: 11 }}>or</span>
            <input
              type="date"
              className="ac-input"
              value={from}
              max={to}
              onChange={(event) => setFrom(event.target.value)}
              style={{ width: 150 }}
              aria-label="From date"
            />
            <span style={{ color: 'var(--ac-faint)' }}>→</span>
            <input
              type="date"
              className="ac-input"
              value={to}
              min={from}
              max={toISODate()}
              onChange={(event) => setTo(event.target.value)}
              style={{ width: 150 }}
              aria-label="To date"
            />
            <ToolbarSpacer />
            <span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>
              {fmtDate(from)}, {fmtDate(to)}
            </span>
          </div>
        </Panel>
      ) : null}

      {/* ---------------- TOTALS ---------------- */}
      {totals ? (
        <Grid cols={4} style={{ marginBottom: 14 }}>
          {Object.entries(totals).map(([key, value]) => (
            <Panel key={key} pad>
              <div className="ac-kpi-label" style={{ marginBottom: 5 }}>
                Total {titleCase(key)}
              </div>
              <div className="ac-kpi-value" style={{ fontSize: 21 }}>
                {fmtMoney(value)}
              </div>
            </Panel>
          ))}
        </Grid>
      ) : null}

      {/* ---------------- RESULT ---------------- */}
      {isLoading ? (
        <Panel>
          <LoadingBlock label="Running report…" height={260} />
        </Panel>
      ) : rawColumns.length === 0 ? (
        <Notice tone="warning" title="This report returned no columns">
          The report could not be generated. Check that the API is reachable and that you have permission to run it.
        </Notice>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          getRowId={(row) => row.id ?? JSON.stringify(row)}
          defaultPageSize={50}
          emptyIcon={FileSpreadsheet}
          emptyTitle="No data for this report"
          emptyText={
            report?.ranged
              ? 'Try widening the date range, or check that records exist for this period.'
              : 'There are no records to report on yet.'
          }
          toolbar={
            <Toolbar>
              <SearchInput value={search} onChange={setSearch} placeholder="Filter results…" width={220} />
              {isFetching ? <span className="ac-spinner" /> : null}
              <ToolbarSpacer />
              <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>
                {fmtNumber(filtered.length)} row{filtered.length === 1 ? '' : 's'}
                {filtered.length !== rows.length ? ` of ${fmtNumber(rows.length)}` : ''}
              </span>
            </Toolbar>
          }
        />
      )}
    </div>
  );
}
